import {useCallback, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  CaseContext,
  updateProtocolSection,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {EMPTY_CASE_CONTEXT} from './caseContextConfig';

interface UseProtocolCaseContextStateOptions {
  caseContext?: CaseContext;
  onCaseContextChange?: (caseContext: CaseContext) => void;
}

export const useProtocolCaseContextState = (
  options: UseProtocolCaseContextStateOptions = {}
) => {
  const {caseContext: caseContextProp, onCaseContextChange} = options;
  const dispatch = useDispatch();
  const [localCaseContext, setLocalCaseContext] =
    useState<CaseContext>(EMPTY_CASE_CONTEXT);

  const storeCaseContext = useSelector(
    (state: RootState) => state.aiReviewV2.protocol?.caseContext
  );
  const isCaseContextUpdated = useRef<boolean>(false);
  const caseContext = useMemo(() => {
    if (!isCaseContextUpdated.current) {
      if (caseContextProp) {
        return caseContextProp;
      }
      if (storeCaseContext) {
        return storeCaseContext;
      }
    }
    return localCaseContext;
  }, [caseContextProp, storeCaseContext, localCaseContext]);

  const updateCaseContext = useCallback(
    (partial: Partial<CaseContext>) => {
      const nextCaseContext = {...caseContext, ...partial};

      if (onCaseContextChange) {
        onCaseContextChange(nextCaseContext);
        return;
      }

      if (storeCaseContext !== undefined) {
        dispatch(
          updateProtocolSection({
            section: 'caseContext',
            data: partial,
          })
        );
        return;
      }
      isCaseContextUpdated.current = true;
      setLocalCaseContext(nextCaseContext);
    },
    [caseContext, dispatch, onCaseContextChange, storeCaseContext]
  );
  return {caseContext, updateCaseContext};
};
